import React, { useState, useEffect } from "react";
import { Modal, ActivityIndicator, Toast } from "antd-mobile";
import { CloseOutline } from "antd-mobile-icons";
import moment from "moment";
import { ApiCore } from "../../api";
import { PhotoSlider } from "react-photo-view";
import "./PmpScaleModal.scss";

const api = new ApiCore();

interface ReportItem {
  id: string;
  name: string;
  code: string;
  reportState: 0 | 1 | 2;
  isLook: boolean;
  updateTime?: string;
}

interface PmpScaleModalProps {
  visible: boolean;
  scaleRow?: ReportItem | null;
  scaleCode?: string;
  visitId?: string;
  subjectCode?: string;
  onClose: () => void;
  onRead?: (id: string) => void;
}

interface VisitItem {
  id: string;
  name: string;
  state: string;
  visitDate: string;
}

interface RecordItem {
  scalaServerRecordId: string;
  restatementState: number;
  createTime: string;
}

interface CrfData {
  crfName: string;
  submitTime: string;
  fields: any[];
  answers: Record<string, any>;
  interpretation?: any[];
}

/** 字段唯一标识：schema 里通常用 fieldId，部分节点用 id / nodeId */
const getFieldKey = (f: any): string => f?.fieldId ?? f?.id ?? f?.nodeId ?? "";

/** 递归收集字段节点：nodeType === 'field' 或 带 type 且有 fieldId/id 的节点 */
const collectFields = (node: any, acc: any[] = []): any[] => {
  if (!node) return acc;
  if (node.nodeType === "field" || (node.type && (node.fieldId || node.id))) {
    acc.push(node);
  }
  const children = node.children || (node.root && node.root.children) || [];
  if (Array.isArray(children)) {
    children.forEach((c: any) => collectFields(c, acc));
  }
  return acc;
};

/** 解析 schemaSnapshot（JSON 字符串，node-tree 结构） */
const parseSchema = (schemaSnapshot?: string): any[] => {
  if (!schemaSnapshot) return [];
  try {
    const schema = JSON.parse(schemaSnapshot);
    const root = schema?.root || schema;
    return collectFields(root);
  } catch {
    return [];
  }
};

/** 从 schemaSnapshot 中提取 interpretation 等级配置 */
const parseInterpretation = (schemaSnapshot?: string): any[] => {
  if (!schemaSnapshot) return [];
  try {
    const schema = JSON.parse(schemaSnapshot);
    return schema?.scaleConfig?.interpretation || [];
  } catch {
    return [];
  }
};

/** 根据总分匹配 interpretation 等级 */
const matchInterpretation = (
  score: number,
  interpretation: any[]
): any | null => {
  return (
    interpretation.find((item) => {
      const min = Number(item.min ?? 0);
      const max = Number(item.max ?? Infinity);
      return score >= min && score <= max;
    }) || null
  );
};

/** interpretation 色号映射（bg: 背景色，text: 文字色） */
const INTERPRETATION_COLOR_MAP: Record<
  string,
  { bg?: string; text: string }
> = {
  green: { bg: "#f6ffed", text: "#389e0d" },
  yellow: { bg: "#fffbe6", text: "#d48806" },
  orange: { bg: "#fff7e6", text: "#d46b08" },
  red: { bg: "#fff1f0", text: "#cf1322" },
  darkred: { bg: "#fff1f0", text: "#a8071a" },
  default: { bg: "#e6f7ff", text: "#096dd9" },
};

const getInterpretationStyle = (color?: string) => {
  const key = color && INTERPRETATION_COLOR_MAP[color] ? color : "default";
  return INTERPRETATION_COLOR_MAP[key];
};

interface ScoreResult {
  score: number;
  maxScore: number;
}

/** 计算量表得分与满分：只汇总带 score 的 likert 选项；无分时 maxScore 为 0 */
const calcScore = (
  fields: any[],
  answers: Record<string, any>
): ScoreResult => {
  let score = 0;
  let maxScore = 0;
  fields.forEach((f) => {
    if (f.type !== "likert") return;
    if (!checkVisible(f, answers)) return;
    const options: any[] = f.options || [];
    const hasScore = options.some(
      (opt: any) => opt.score !== undefined && opt.score !== null
    );
    if (!hasScore) return;
    const key = getFieldKey(f);
    const answer = answers?.[key];
    maxScore += Math.max(
      ...options.map((opt: any) => Number(opt.score))
    );
    if (answer !== undefined && answer !== null) {
      const selected = options.find(
        (opt: any) => String(opt.value) === String(answer)
      );
      if (selected && selected.score !== undefined && selected.score !== null) {
        score += Number(selected.score);
      }
    }
  });
  return { score, maxScore };
};

const fmtTime = (ts: any): string => {
  if (!ts) return "";
  const m = moment(/\D/.test(String(ts)) ? ts : Number(ts));
  return m.isValid() ? m.format("YYYY-MM-DD HH:mm:ss") : String(ts);
};

const PmpScaleModal = (props: PmpScaleModalProps) => {
  const { visible, scaleRow, scaleCode, visitId, subjectCode, onClose, onRead } =
    props;

  const [loading, setLoading] = useState(false);
  const [visitList, setVisitList] = useState<VisitItem[]>([]);
  const [currentVisitId, setCurrentVisitId] = useState<string>("");
  const [recordList, setRecordList] = useState<RecordItem[]>([]);
  const [currentRecordId, setCurrentRecordId] = useState<string>("");
  const [crfData, setCrfData] = useState<CrfData | null>(null);

  // 图片预览
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  // 拉取某访视下的填报记录，默认选中最新一条
  const loadRecords = async (vId: string) => {
    if (!vId || !scaleCode || !subjectCode) return;
    try {
      const { data } = await api.getReportedById({
        scaleCode,
        visitId: vId,
      });
      const list: RecordItem[] = (data || [])
        .slice()
        .sort(
          (a: any, b: any) =>
            new Date(a.createTime).getTime() - new Date(b.createTime).getTime()
        )
        .map((r: any) => ({
          scalaServerRecordId: r.scalaServerRecordId,
          restatementState: r.restatementState,
          createTime: fmtTime(r.createTime),
        }));
      setRecordList(list);
      // 默认选中最新一条（数组最后）
      const latest = list[list.length - 1];
      if (latest) {
        setCurrentRecordId(latest.scalaServerRecordId);
        loadCrf(latest.scalaServerRecordId);
      } else {
        setCurrentRecordId("");
        setCrfData(null);
      }
    } catch (e) {
      Toast.fail("获取填报记录失败");
    }
  };

  // 拉取 PMP 量表表单详情
  const loadCrf = async (recordId: string) => {
    if (!recordId) return;
    setLoading(true);
    try {
      const { data } = await api.getPmpCrfRecord(recordId);
      const schemaSnapshot = data?.schemaSnapshot;
      const dataJson = data?.dataJson;
      const fields = parseSchema(schemaSnapshot);
      let answers: Record<string, any> = {};
      try {
        answers = dataJson ? JSON.parse(dataJson) : {};
      } catch {
        answers = {};
      }
      setCrfData({
        crfName: data?.crfName || "",
        submitTime: fmtTime(data?.submitTime),
        fields,
        answers,
        interpretation: parseInterpretation(schemaSnapshot),
      });
    } catch (e) {
      Toast.fail("获取量表详情失败");
      setCrfData(null);
    } finally {
      setLoading(false);
    }
  };

  // 拉取访视列表
  const loadVisits = async () => {
    if (!scaleCode || !subjectCode) return;
    try {
      const { data } = await api.getVisitsByScaleCode({ scaleCode, subjectCode });
      const list: VisitItem[] = (data || []).map((v: any) => ({
        id: v.id?.toString(),
        name: v.name,
        state: v.state,
        visitDate: v.visitDate,
      }));
      setVisitList(list);
      const target =
        list.find((v) => v.id === visitId) || list[0] || null;
      if (target) {
        setCurrentVisitId(target.id);
        loadRecords(target.id);
      }
    } catch (e) {
      Toast.fail("获取访视列表失败");
    }
  };

  // 打开弹窗：标记已读 + 拉取数据
  useEffect(() => {
    if (!visible) return;
    if (scaleRow && subjectCode) {
      api
        .updateReadStatus(Number(scaleRow.id), subjectCode)
        .catch(() => {});
      onRead?.(scaleRow.id);
    }
    setCrfData(null);
    setRecordList([]);
    setCurrentRecordId("");
    loadVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const selectVisit = (vId: string) => {
    if (vId === currentVisitId) return;
    setCurrentVisitId(vId);
    loadRecords(vId);
  };

  const selectRecord = (r: RecordItem) => {
    if (r.scalaServerRecordId === currentRecordId) return;
    setCurrentRecordId(r.scalaServerRecordId);
    loadCrf(r.scalaServerRecordId);
  };

  const openPreview = (urls: string[], index: number) => {
    if (!urls || !urls.length) return;
    setPreviewImages(urls);
    setPreviewIndex(index);
    setPreviewVisible(true);
  };

  return (
    <Modal
      visible={visible}
      popup
      animationType="slide-up"
      onClose={onClose}
    >
      <div className="pmp-modal">
        <div className="pmp-header">
          <div className="pmp-header-title">{scaleRow?.name || "量表详情"}</div>
          <div className="pmp-header-close" onClick={onClose}>
            <CloseOutline />
          </div>
        </div>

        <div className="pmp-body">
          {/* 左侧：访视切换 + 时间轴 */}
          <div className="pmp-left">
            <div className="pmp-section-label">访视</div>
            <div className="pmp-visits">
              {visitList.map((v) => (
                <div
                  key={v.id}
                  className={`pmp-visit ${
                    v.id === currentVisitId ? "active" : ""
                  }`}
                  onClick={() => selectVisit(v.id)}
                >
                  {v.name}
                </div>
              ))}
              {visitList.length === 0 && (
                <div className="pmp-empty-tip">暂无访视</div>
              )}
            </div>

            <div className="pmp-section-label">填报记录</div>
            <div className="pmp-timeline">
              {recordList.map((r) => (
                <div
                  key={r.scalaServerRecordId}
                  className={`pmp-record ${
                    r.scalaServerRecordId === currentRecordId ? "active" : ""
                  }`}
                  onClick={() => selectRecord(r)}
                >
                  <span className="pmp-record-dot" />
                  <span className="pmp-record-time">{r.createTime}</span>
                </div>
              ))}
              {recordList.length === 0 && (
                <div className="pmp-empty-tip">该访视暂无填报</div>
              )}
            </div>
          </div>

          {/* 右侧：表单 */}
          <div className="pmp-right">
            <ActivityIndicator
              animating={loading}
              size={"small"}
              text={"加载中"}
              toast
            />
            {!loading && crfData && (
              <div className="pmp-form">
                {crfData.crfName && (
                  <div className="pmp-crf-name">{crfData.crfName}</div>
                )}
                {(() => {
                  const { score, maxScore } = calcScore(
                    crfData.fields,
                    crfData.answers || {}
                  );
                  if (maxScore <= 0) return null;
                  const matched = matchInterpretation(
                    score,
                    crfData.interpretation || []
                  );
                  const label = matched?.label;
                  const style = getInterpretationStyle(matched?.color);
                  return (
                    <div className="pmp-total-score">
                      得分：{score}/满分{maxScore}
                      {label && (
                        <span
                          className="pmp-interpretation"
                          style={{
                            color: style.text,
                            backgroundColor: style.bg,
                          }}
                        >
                          {label}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {crfData.fields.map((f) => {
                  const fieldKey = getFieldKey(f);
                  return (
                    <FieldRenderer
                      key={fieldKey || f.nodeId}
                      field={f}
                      answer={crfData.answers?.[fieldKey]}
                      allAnswers={crfData.answers || {}}
                      onPreview={openPreview}
                    />
                  );
                })}

                {crfData.fields.length === 0 && (
                  <div className="pmp-empty-tip">无表单字段</div>
                )}
              </div>
            )}
            {!loading && !crfData && (
              <div className="pmp-empty-tip">暂无填报内容</div>
            )}
          </div>
        </div>

        <PhotoSlider
          photoClosable
          images={previewImages.map((src) => ({ src }))}
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          index={previewIndex}
          onIndexChange={(i: number) => setPreviewIndex(i)}
        />
      </div>
    </Modal>
  );
};

interface FieldRendererProps {
  field: any;
  answer: any;
  allAnswers: Record<string, any>;
  onPreview: (urls: string[], index: number) => void;
}

/** 解析显隐规则：支持 { when: fieldId, is: value | value[] } / { when: fieldId, isNot: value } */
const checkVisible = (field: any, allAnswers: Record<string, any>): boolean => {
  const visible = field?.visible;
  if (!visible) return true;
  const whenKey = visible.when;
  if (!whenKey) return true;
  const actual = allAnswers?.[whenKey];
  const actualStr = String(actual);
  if (visible.is !== undefined) {
    if (Array.isArray(visible.is)) {
      return visible.is.some((v: any) => String(v) === actualStr);
    }
    return actualStr === String(visible.is);
  }
  if (visible.isNot !== undefined) {
    if (Array.isArray(visible.isNot)) {
      return !visible.isNot.some((v: any) => String(v) === actualStr);
    }
    return actualStr !== String(visible.isNot);
  }
  return true;
};

const FieldRenderer = ({ field, answer, allAnswers, onPreview }: FieldRendererProps) => {
  if (!checkVisible(field, allAnswers)) return null;
  const title = field.label ?? field.title ?? field.name ?? "";
  const type = field.type;

  // 单选 / 量表（likert）
  if (type === "likert") {
    const options = field.options || [];
    return (
      <div className="pmp-field">
        <div className="pmp-field-label">{title}</div>
        <div className="pmp-options">
          {options.map((opt: any) => {
            const selected = String(opt.value) === String(answer);
            return (
              <div
                className={`pmp-option ${selected ? "selected" : ""}`}
                key={String(opt.value)}
              >
                <span className="pmp-option-dot" />
                <span className="pmp-option-label">{opt.label}</span>
                {opt.score !== undefined && opt.score !== null && (
                  <span className="pmp-option-score">得分 {opt.score}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 图片上传（file + picture）：答案可能是单张 URL 字符串或 URL 数组
  if (type === "file" && field.listType === "picture") {
    let urls: string[] = [];
    if (typeof answer === "string" && answer) {
      urls = [answer];
    } else if (Array.isArray(answer)) {
      urls = answer.filter((u) => typeof u === "string" && u);
    }
    return (
      <div className="pmp-field">
        <div className="pmp-field-label">{title}</div>
        <div className="pmp-img-grid">
          {urls.map((url, idx) => (
            <img
              key={idx}
              src={url}
              className="pmp-thumb"
              onClick={() => onPreview(urls, idx)}
              alt=""
            />
          ))}
          {urls.length === 0 && (
            <span className="pmp-empty-tip">无图片</span>
          )}
        </div>
      </div>
    );
  }

  // 兜底：展示原始值
  const raw =
    answer === undefined || answer === null
      ? ""
      : typeof answer === "object"
      ? JSON.stringify(answer)
      : String(answer);
  return (
    <div className="pmp-field">
      <div className="pmp-field-label">{title}</div>
      <div className="pmp-value">{raw || "—"}</div>
    </div>
  );
};

export default PmpScaleModal;
